import React, {
  VoidFunctionComponent,
  useState,
  ChangeEventHandler,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import cx from 'classnames'
import { l, NumberInputV2 } from '@manychat/manyui'

import { Select } from 'components/forms/Select/Select'
import { useDidMountEffect } from 'utils/commonHooks'

import {
  Unit,
  UNIT_OPTIONS,
  DEFAULT_MAX_VALUE,
  DEFAULT_MIN_VALUE,
  SECONDS_IN_DAY,
  SECONDS_IN_HOUR,
  SECONDS_IN_MINUTE,
} from './constants'
import { formatDelay, getUnit, unitValueToSeconds } from './utils'

import cm from './DelayPicker.module.css'

interface DelayPickerProps {
  delay: number
  [Unit.IMMEDIATELY]?: boolean
  [Unit.DAYS]?: boolean
  [Unit.MINUTES]?: boolean
  [Unit.HOURS]?: boolean
  disabled?: boolean
  className?: string
  maxDelay?: number
  onChange: (delay: number) => void
}

export const DelayPicker: VoidFunctionComponent<DelayPickerProps> = ({
  delay,
  onChange,
  disabled,
  className,
  maxDelay,
  ...units
}) => {
  const [unit, setUnit] = useState(getUnit(delay))
  const [value, setValue] = useState(formatDelay(delay))

  useDidMountEffect(() => {
    onChange(delay)
  })

  const maxValue = useMemo(() => {
    if (!maxDelay) return DEFAULT_MAX_VALUE

    switch (unit) {
      case Unit.IMMEDIATELY:
        return 0
      case Unit.DAYS:
        return maxDelay / SECONDS_IN_DAY
      case Unit.HOURS:
        return maxDelay / SECONDS_IN_HOUR
      default:
        return maxDelay * SECONDS_IN_MINUTE
    }
  }, [unit, maxDelay])

  const emitChange = useCallback(() => {
    const currentDelay = unitValueToSeconds(unit, value)

    if (currentDelay !== delay) {
      onChange(currentDelay)
    }
  }, [onChange, delay, unit, value])

  useEffect(() => {
    if (value > maxValue) setValue(maxValue)
    emitChange()
  }, [unit, value, maxValue, emitChange])

  const setAttr: ChangeEventHandler<HTMLSelectElement> = useCallback((event) => {
    const { value } = event.target
    setUnit(value as Unit)
  }, [])

  const onValueChange = useCallback(
    (value: string | number) => {
      let formattedValue = Number(Number(value).toFixed())
      if (formattedValue < DEFAULT_MIN_VALUE) {
        formattedValue = DEFAULT_MIN_VALUE
      }
      if (maxDelay && formattedValue > maxDelay) {
        formattedValue = maxDelay
      }
      setValue(formattedValue)
    },
    [maxDelay],
  )

  const options = useMemo(() => {
    const options = UNIT_OPTIONS.filter(({ value }) => units[value])

    return (options.length ? options : UNIT_OPTIONS).map((option) => ({
      label: l.getString(option.label),
      value: option.value,
    }))
  }, [units])

  return (
    <div className={cx(cm.wrapper, className)}>
      {unit !== Unit.IMMEDIATELY && (
        <NumberInputV2
          className={cx(cm.numberInput, 'm-r-xs')}
          value={Number(value).toFixed()}
          onValueChange={onValueChange}
          min={0}
          max={maxValue}
          name="value"
          disabled={disabled}
          ariaLabel={l.getString('Delay time')}
          upAriaLabel={l.getString('Increase delay time')}
          downAriaLabel={l.getString('Decrease delay time')}
        />
      )}
      <Select
        className={cm.selectInput}
        value={unit}
        onChange={setAttr}
        options={options}
        name="unit"
        disabled={disabled}
      />
    </div>
  )
}
